import type {
  CrmContactSummarySignal,
  CrmContactSummarySignalTone,
} from "@ease/crm/contact-summary-signals";
import { cn } from "@/lib/utils";

interface CrmContactGlanceSignalsProps {
  signals: CrmContactSummarySignal[];
  className?: string;
  itemClassName?: string;
}

export function CrmContactGlanceSignals({
  signals,
  className,
  itemClassName,
}: CrmContactGlanceSignalsProps) {
  if (signals.length === 0) return null;

  return (
    <div className={cn("mt-3 flex flex-wrap gap-2", className)} aria-label="Contact summary signals">
      {signals.map((signal) => (
        <span
          key={signal.key}
          className={cn(
            "inline-flex max-w-[16rem] flex-col rounded-md border px-2.5 py-1.5 text-xs leading-tight",
            getCrmContactGlanceSignalClasses(signal.tone),
            itemClassName,
          )}
          title={signal.detail ?? undefined}
        >
          <span className="font-semibold">{signal.label}</span>
          <span className="mt-0.5 block min-w-0 truncate text-[var(--color-foreground)]">{signal.value}</span>
          {signal.detail ? (
            <span className="mt-0.5 block min-w-0 truncate text-[11px] text-[var(--color-muted-foreground)]">
              {signal.detail}
            </span>
          ) : null}
        </span>
      ))}
    </div>
  );
}

export function getCrmContactGlanceSignalClasses(tone: CrmContactSummarySignalTone): string {
  if (tone === "success") {
    return "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]";
  }
  if (tone === "warning") {
    return "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]";
  }
  if (tone === "danger") {
    return "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]";
  }
  if (tone === "info") {
    return "border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 text-[var(--color-primary)]";
  }
  return "border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-muted-foreground)]";
}
